/* eslint-disable react/prop-types */
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import {
  Link, useHistory, useLocation, useParams
} from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowLeft, ArrowRight, ArrowUpRight, CalendarClock,
  Building2, CircleGauge, CirclePower, ClipboardList, Fuel, Gauge, Loader2,
  MapPin, Pencil, Radio, RefreshCw, Settings2, ShieldCheck, TimerReset, Truck,
  UserRound, Wrench,
} from 'lucide-react';
import ROUTE from 'src/utils/routeConstants';
import useAuth from 'src/context/AuthContext';
import { isAdminUser } from 'src/utils/getInitials';
import { isFleetOnlyModeEnabled } from 'src/utils/appMode';
import { useTheme as useAppTheme } from 'src/context/ThemeContext';
import PageBreadcrumb, { hasNestedLinks } from 'src/features/tyre-management/components/PageBreadcrumb';
import { Button } from 'src/features/tyre-management/components/ui/button';
import { Badge } from 'src/features/tyre-management/components/ui/badge';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from 'src/features/tyre-management/components/ui/card';
import { Skeleton } from 'src/features/tyre-management/components/ui/skeleton';
import useVehicleOverview from './hooks/useVehicleOverview';
import SectionState from './components/SectionState';
import StatePill from './components/StatePill';
import TyrePlan from './components/TyrePlan';
import RecordServiceDialog from './components/RecordServiceDialog';
import VehicleStatusDialog from './components/VehicleStatusDialog';
import TyreSetupJourney from './components/TyreSetupJourney';
import {
  formatDateTime, formatNumber, formatRelativeAge, titleCase,
} from './lib/utils';
import { getTyreSetupJourney } from './lib/tyreSetupJourney';
import 'src/features/tyre-management/tyre-management.css';
import './vehicle-details.css';

const sections = [
  ['now', 'Overview'], ['fleet-assignment', 'Fleet & assignment'], ['tyres', 'Tyres'],
  ['usage-upkeep', 'Usage & upkeep'], ['activity', 'Activity'],
  ['vehicle-record', 'Vehicle information'],
];

const vehicleCrumbs = (registration) => [
  { label: 'Vehicle Management' },
  { label: 'Vehicles', href: ROUTE.vehicles },
  { label: registration || 'Vehicle', mono: Boolean(registration) },
];

const severityIcon = (priority) => {
  if (priority <= 2) return AlertTriangle;
  if (priority === 3) return Settings2;
  return CalendarClock;
};

const fieldLabels = {
  registration_num: 'Registration number',
  asset_code: 'Asset code',
  model: 'Model',
  category: 'Category',
  vehicle_type: 'Movement type',
  asset_type: 'Asset ownership',
  site: 'Site',
  department: 'Department',
  group: 'Vehicle group',
  contractor: 'Contractor',
  po_number: 'PO number',
  fuel_type: 'Fuel type',
  tank_capacity: 'Tank capacity',
  tank_count: 'Tank count',
  standard_mileage: 'Standard mileage',
  allowed_variance: 'Allowed variance',
  monthly_fixed_engine_hours: 'Fixed engine hours',
  telematics_enabled: 'Telematics enabled',
  telematics_category: 'Telematics category',
  fleet_service_provider: 'Fleet service provider',
  active: 'Active',
  created_at: 'Created',
  updated_at: 'Last updated',
};

const readinessTones = {
  slate: 'bg-slate-100 text-slate-600',
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
};

const attentionTones = {
  critical: 'border-red-200 bg-red-50/80 text-red-700',
  warning: 'border-amber-200 bg-amber-50/80 text-amber-700',
  info: 'border-blue-200 bg-blue-50/80 text-blue-700',
};

const ReadinessCard = ({
  icon: Icon, label, value, note, tone = 'slate',
}) => (
  <Card className="shadow-sm">
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-2 truncate text-2xl font-extrabold tracking-tight text-slate-950">{value}</p>
          <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-500">{note}</p>
        </div>
        <span className={`shrink-0 rounded-xl p-2.5 ${readinessTones[tone]}`}><Icon className="h-5 w-5" /></span>
      </div>
    </CardContent>
  </Card>
);

const OperatingMetric = ({
  icon: Icon, label, value, note, tone = 'slate',
}) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
      <span className={`rounded-lg p-2 ${readinessTones[tone]}`}><Icon className="h-4 w-4" /></span>
      {label}
    </div>
    <p className="mt-4 text-xl font-extrabold text-slate-950">{value}</p>
    <p className="mt-1 text-sm leading-5 text-slate-500">{note}</p>
  </div>
);

const DetailList = ({ items }) => (
  <dl className="vd-detail-list">
    {items.map((item) => (
      <div key={item.label}>
        <dt>{item.label}</dt>
        <dd className={item.mono ? 'vd-mono' : ''}>{item.value || 'Not recorded'}</dd>
      </div>
    ))}
  </dl>
);

const FleetPanel = ({
  icon: Icon, eyebrow, title, description, children,
}) => (
  <section className="vd-fleet-panel">
    <div className="vd-fleet-panel__heading">
      <span><Icon /></span>
      <div>
        <p>{eyebrow}</p>
        <h3>{title}</h3>
      </div>
    </div>
    {description && <p className="vd-fleet-panel__description">{description}</p>}
    {children}
  </section>
);

const VehicleDetailsV2 = () => {
  const { vehicleId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { getUser } = useAuth();
  const appSettings = useSelector((state) => state.settings.appSettings);
  const { mode } = useAppTheme();
  const isAdmin = isAdminUser(getUser);
  const tyreWorkspaceAvailable = !isFleetOnlyModeEnabled(appSettings);
  const [period, setPeriod] = useState('30d');
  const [serviceOpen, setServiceOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const {
    data, loading, updating, error, reload,
  } = useVehicleOverview(vehicleId, period);

  const navigateAction = (action) => {
    if (!action) return;
    if (action.href?.startsWith(ROUTE.tyreV2Setup)
      && (!isAdmin || !tyreWorkspaceAvailable)) return;
    if (action.href?.startsWith(ROUTE.tyreManagementV2)
      && !tyreWorkspaceAvailable) return;
    if (action.id === 'record_service' || action.kind === 'command_dialog') {
      setServiceOpen(true);
      return;
    }
    if (action.kind === 'confirmation') {
      setStatusOpen(true);
      return;
    }
    if (action.href) history.push(action.href);
  };

  const recordFieldMap = useMemo(() => (data?.vehicle?.record_fields || []).reduce((fields, field) => ({
    ...fields,
    [field.key]: field,
  }), {}), [data]);

  if (loading && !data) {
    return (
      <main className="tyre-management-v2 vehicle-details-v2 min-h-[calc(100vh-64px)] px-4 py-5 sm:px-6 lg:px-8" data-theme={mode} aria-label="Loading vehicle details">
        <div className="w-full space-y-5">
          <PageBreadcrumb items={vehicleCrumbs()} />
          <Card>
            <CardContent className="p-6">
              <Skeleton height={34} width={260} />
              <Skeleton className="mt-3" height={16} width={180} />
            </CardContent>
          </Card>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Card key={index}>
                <CardContent className="p-5">
                  <Skeleton height={18} width="45%" />
                  <Skeleton className="mt-3" height={30} width="65%" />
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr]">
            <Card><CardContent className="p-6"><Skeleton height={260} /></CardContent></Card>
            <Card><CardContent className="p-6"><Skeleton height={260} /></CardContent></Card>
          </div>
        </div>
      </main>
    );
  }

  if (error && !data) {
    return (
      <main className="tyre-management-v2 vehicle-details-v2 min-h-[calc(100vh-64px)] px-4 py-5 sm:px-6 lg:px-8" data-theme={mode}>
        <div className="w-full space-y-5">
          <PageBreadcrumb items={vehicleCrumbs()} />
          <div className="grid min-h-[60vh] place-items-center">
            <Card className="w-full max-w-xl">
              <CardContent className="flex flex-col items-center p-10 text-center">
                <span className="rounded-full bg-red-50 p-3 text-red-600"><AlertTriangle /></span>
                <h1 className="mt-4 text-xl font-bold text-slate-950">Vehicle details unavailable</h1>
                <p className="mt-2 text-sm text-slate-500">{error}</p>
                <div className="mt-6 flex gap-2">
                  <Button variant="outline" onClick={() => history.push(ROUTE.vehicles)}>Back to vehicles</Button>
                  <Button onClick={reload}>Try again</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    );
  }

  const { vehicle } = data;
  const crumbs = vehicleCrumbs(vehicle.registration_num);
  const overview = data.sections;
  const now = overview.now?.data;
  const attention = overview.attention?.data?.items || [];
  const usage = overview.usage?.data;
  const service = overview.service?.data;
  const breakdowns = overview.breakdowns?.data;
  const activity = overview.activity?.data?.events || [];
  const tyreData = overview.tyres?.data;
  const fleet = overview.fleet?.data;
  const assignment = fleet?.assignment || {};
  const tracking = fleet?.tracking || {};
  const canEdit = data.capabilities.includes('edit_vehicle');
  const canService = data.capabilities.includes('record_service') && service;
  const justCreated = location.state?.vehicleCreated === true
    && String(location.state?.vehicleId) === String(vehicle.id);
  const tyreManagementEnabled = tyreData?.variant !== 'disabled'
    && (vehicle.tyre_management_enabled === true
      || tyreData?.variant === 'v2_setup_required'
      || Boolean(tyreData?.layout));
  const tyreSetupJourney = getTyreSetupJourney({
    vehicleId: vehicle.id,
    tyreManagementEnabled,
    layout: tyreData?.layout,
    isAdmin,
    canEdit,
    justCreated,
    tyreWorkspaceAvailable,
  });
  const serviceUnit = service?.meter_kind === 'engine_hours' ? 'hr' : 'km';
  let serviceValue = 'Setup required';
  if (service?.state === 'overdue') serviceValue = `${formatNumber(Math.abs(Number(service.remaining_value)))} ${serviceUnit} overdue`;
  else if (service?.remaining_value !== null && service?.remaining_value !== undefined) serviceValue = `${formatNumber(service.remaining_value)} ${serviceUnit} remaining`;
  let serviceTone = service ? 'emerald' : 'slate';
  if (service?.state === 'due_soon') serviceTone = 'amber';
  if (service?.state === 'overdue') serviceTone = 'red';
  let breakdownTone = breakdowns ? 'emerald' : 'slate';
  if (breakdowns?.open_count) breakdownTone = 'red';

  let tyreValue = 'Unavailable';
  let tyreNote = overview.tyres?.reason || 'Tyre health is unavailable.';
  let tyreTone = 'slate';
  if (tyreData?.variant === 'v2_setup_required') {
    tyreValue = 'Setup needed';
    tyreTone = 'amber';
  } else if (tyreData?.layout) {
    tyreValue = `${tyreData.layout.occupied_positions}/${tyreData.layout.mandatory_positions} fitted`;
    tyreNote = `${tyreData.open_alert_count || 0} open tyre alert${tyreData.open_alert_count === 1 ? '' : 's'}`;
    tyreTone = tyreData.open_alert_count ? 'amber' : 'emerald';
  } else if (tyreData?.variant === 'legacy') {
    tyreValue = 'Legacy records';
    tyreTone = 'amber';
  }

  const currentMeterValue = now?.primary_meter
    ? `${formatNumber(now.primary_meter.value, { maximumFractionDigits: 1 })} ${now.primary_meter.unit || ''}`
    : 'Unavailable';

  const fieldValue = (key) => {
    const field = recordFieldMap[key];
    return field?.state === 'recorded' ? field.display_value : null;
  };
  const assignmentItems = [
    { label: 'Site', value: assignment.site?.name || fieldValue('site') },
    { label: 'Department', value: assignment.department?.name || fieldValue('department') },
    { label: 'Vehicle group', value: assignment.group?.name || fieldValue('group') },
    { label: 'Contractor', value: assignment.contractor?.name || fieldValue('contractor') },
    { label: 'PO number', value: assignment.po_number || fieldValue('po_number') },
  ];
  const identityItems = ['registration_num', 'asset_code', 'model', 'category', 'vehicle_type', 'asset_type']
    .map((key) => ({
      label: fieldLabels[key],
      value: fieldValue(key),
      mono: ['registration_num', 'asset_code'].includes(key),
    }));
  const tankCapacity = Number(fieldValue('tank_capacity'));
  const tankCount = Number(fieldValue('tank_count'));
  const standardMileage = Number(fieldValue('standard_mileage'));
  const allowedVariance = Number(fieldValue('allowed_variance'));
  const efficiencyUnit = vehicle.vehicle_type === 'Non Movable' ? 'hr/L' : 'km/L';
  const operationItems = [
    { label: fieldLabels.fuel_type, value: fieldValue('fuel_type') },
    { label: fieldLabels.tank_capacity, value: tankCapacity > 0 ? `${formatNumber(tankCapacity)} L` : null },
    { label: fieldLabels.tank_count, value: tankCount > 0 ? formatNumber(tankCount) : null },
    { label: fieldLabels.standard_mileage, value: standardMileage > 0 ? `${formatNumber(standardMileage)} ${efficiencyUnit}` : null },
    { label: fieldLabels.allowed_variance, value: allowedVariance > 0 ? formatNumber(allowedVariance) : null },
  ];
  if (vehicle.vehicle_type === 'Non Movable') {
    const fixedHours = Number(fieldValue('monthly_fixed_engine_hours'));
    operationItems.push({
      label: fieldLabels.monthly_fixed_engine_hours,
      value: fixedHours > 0 ? `${formatNumber(fixedHours)} hr/month` : null,
    });
  }
  const trackingLabel = {
    connected: 'Tracking connected',
    setup_required: 'Setup required',
    not_enabled: 'Tracking not enabled',
  }[tracking.state] || 'Tracking status unavailable';
  let trackingPillState = tracking.state || 'unavailable';
  if (tracking.state === 'connected') trackingPillState = 'active';
  if (tracking.state === 'setup_required') trackingPillState = 'warning';

  return (
    <main className="tyre-management-v2 vehicle-details-v2 min-h-[calc(100vh-64px)] px-4 py-5 sm:px-6 lg:px-8" data-theme={mode}>
      <Helmet>
        <title>
          {vehicle.registration_num}
          {' '}
          | Vehicle Details
        </title>
      </Helmet>
      <div className="w-full space-y-6">
        <PageBreadcrumb items={crumbs} />
        <Card className="overflow-visible shadow-sm">
          <CardContent className="p-0">
            <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-start gap-3 sm:items-center">
                {!hasNestedLinks(crumbs) && (
                  <Button variant="ghost" size="icon" aria-label="Back to vehicles" onClick={() => history.push(ROUTE.vehicles)}>
                    <ArrowLeft />
                  </Button>
                )}
                <span className="hidden shrink-0 rounded-xl bg-primary/10 p-3 text-primary sm:inline-flex"><Truck className="h-6 w-6" /></span>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Fleet vehicle</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <h1 className="vd-mono truncate text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">{vehicle.registration_num}</h1>
                    <StatePill value={vehicle.active ? 'active' : 'inactive'} />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                    <span>{vehicle.model || 'Model not recorded'}</span>
                    {vehicle.site && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {vehicle.site.name}
                    </span>
                    )}
                    {vehicle.asset_code && (
                    <span>
                      Asset
                      {' '}
                      {vehicle.asset_code}
                    </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {updating && (
                <Badge variant="info" className="gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Updating
                </Badge>
                )}
                <Button variant="outline" size="icon" aria-label="Refresh vehicle overview" onClick={reload}><RefreshCw /></Button>
                {canEdit && (
                <Button asChild>
                  <Link to={`/app/vehicles/vehicle-list/${vehicle.id}/edit`}>
                    <Pencil />
                    Edit vehicle
                  </Link>
                </Button>
                )}
                {canEdit && (
                <Button variant="outline" onClick={() => setStatusOpen(true)}>
                  <CirclePower />
                  {vehicle.active ? 'Deactivate' : 'Activate'}
                </Button>
                )}
              </div>
            </div>
            <nav className="flex gap-1 overflow-x-auto border-t border-slate-200 px-4 py-2" aria-label="Vehicle details sections">
              {sections.map(([id, label]) => <a key={id} href={`#${id}`} className="whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">{label}</a>)}
            </nav>
          </CardContent>
        </Card>

        {error && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Refresh failed:
            {error}
            . Showing the last successful overview.
          </span>
        </div>
        )}

        <TyreSetupJourney journey={tyreSetupJourney} justCreated={justCreated} />

        <section id="now" className="vd-section-anchor space-y-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Vehicle readiness</p>
            <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-950">Current operating picture</h2>
            <p className="mt-1 text-sm text-slate-500">The latest accepted data and maintenance state for this vehicle.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <ReadinessCard icon={vehicle.vehicle_type === 'Non Movable' ? TimerReset : Gauge} label="Current meter" value={currentMeterValue} note={now?.primary_meter ? formatRelativeAge(now.freshness_seconds) : overview.now?.reason} tone={now?.primary_meter ? 'emerald' : 'slate'} />
            <ReadinessCard icon={CircleGauge} label="Tyre readiness" value={tyreValue} note={tyreNote} tone={tyreTone} />
            <ReadinessCard icon={Wrench} label="Next service" value={serviceValue} note={service?.last_service ? `Last service ${formatDateTime(service.last_service.performed_at)}` : overview.service?.reason} tone={serviceTone} />
            <ReadinessCard icon={AlertTriangle} label="Breakdowns" value={`${breakdowns?.open_count ?? '—'} open`} note={breakdowns ? `${breakdowns.period_count} in the selected period` : overview.breakdowns?.reason} tone={breakdownTone} />
          </div>
        </section>

        <Card id="fleet-assignment" className="vd-section-anchor overflow-hidden">
          <CardHeader className="flex-col gap-3 space-y-0 border-b border-slate-100 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Fleet management</p>
              <CardTitle className="mt-2">Where this vehicle belongs and how it is tracked</CardTitle>
              <CardDescription className="mt-1">Assignment, responsibility, and tracking are shown together so they are easy to verify.</CardDescription>
            </div>
            {data.actions.find((action) => action.id === 'open_fleet_statistics') && (
            <Button variant="outline" onClick={() => navigateAction(data.actions.find((action) => action.id === 'open_fleet_statistics'))}>
              <Activity />
              Open fleet management
            </Button>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="vd-fleet-grid">
              <FleetPanel icon={Building2} eyebrow="Assignment" title={assignment.site?.name || vehicle.site?.name || 'No site assigned'}>
                <DetailList items={assignmentItems} />
              </FleetPanel>
              <FleetPanel icon={Radio} eyebrow="Vehicle tracking" title={trackingLabel} description={tracking.message || 'Tracking information is unavailable.'}>
                <div className="vd-tracking-status">
                  <StatePill value={trackingPillState} label={trackingLabel} />
                  <span>{tracking.provider || 'No tracking provider selected'}</span>
                </div>
                <DetailList items={[
                  { label: 'Tracking provider', value: tracking.provider },
                  { label: 'Fleet category', value: tracking.category },
                  { label: 'Device connection', value: tracking.device_connected ? 'Connected' : 'Not connected' },
                ]}
                />
                {canEdit && tracking.state !== 'connected' && (
                <Button className="mt-4" variant="outline" onClick={() => history.push(`/app/vehicles/vehicle-list/${vehicle.id}/edit`)}>
                  <Settings2 />
                  Set up tracking
                </Button>
                )}
              </FleetPanel>
              <FleetPanel icon={UserRound} eyebrow="Responsibility" title={assignment.contractor?.name || 'Managed internally'} description={assignment.contractor ? 'This contractor is responsible for the vehicle.' : 'No external contractor is assigned.'}>
                <DetailList items={[
                  { label: 'Ownership', value: fieldValue('asset_type') },
                  { label: 'Contractor', value: assignment.contractor?.name || fieldValue('contractor') },
                  { label: 'PO number', value: assignment.po_number || fieldValue('po_number') },
                ]}
                />
              </FleetPanel>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr]">
          <Card>
            <CardHeader className="flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle>Action centre</CardTitle>
                <CardDescription className="mt-1">Priorities that need a decision or setup.</CardDescription>
              </div>
              <span className="rounded-xl bg-primary/10 p-2.5 text-primary"><Activity className="h-5 w-5" /></span>
            </CardHeader>
            <CardContent>
              {attention.length ? (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" role="region" aria-label="Vehicle attention items">
                  {attention.slice(0, 6).map((item) => {
                    const Icon = severityIcon(item.priority);
                    return (
                      <article key={item.id} className={`flex min-h-[178px] flex-col rounded-xl border p-4 ${attentionTones[item.severity] || attentionTones.info}`}>
                        <span className="mb-4 inline-flex w-fit rounded-lg bg-white/80 p-2 shadow-sm"><Icon className="h-4 w-4" /></span>
                        <h3 className="text-sm font-extrabold text-slate-950">{item.title}</h3>
                        <p className="mt-1 flex-1 text-sm leading-5 text-slate-600">{item.detail}</p>
                        {item.action
                          && (!item.action.href?.startsWith(ROUTE.tyreV2Setup) || isAdmin)
                          && (!item.action.href?.startsWith(ROUTE.tyreManagementV2) || tyreWorkspaceAvailable) && (
                        <Button className="mt-4 w-full justify-between" size="sm" variant="outline" onClick={() => navigateAction(item.action)}>
                          {item.action.label}
                          <ArrowRight />
                        </Button>
                        )}
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-emerald-200 bg-emerald-50/60 p-8 text-center">
                  <span className="rounded-full bg-white p-3 text-emerald-600 shadow-sm"><ShieldCheck /></span>
                  <h3 className="mt-4 font-bold text-slate-950">No current action identified</h3>
                  <p className="mt-1 max-w-md text-sm text-slate-500">{overview.attention?.data?.unavailable_domains?.length ? `Unavailable: ${overview.attention.data.unavailable_domains.join(', ')}` : 'All available domains report no actionable condition.'}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card id="tyres" className="vd-section-anchor overflow-hidden">
            <CardHeader className="flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle>Tyre workspace</CardTitle>
                <CardDescription className="mt-1">Layout, fitment, and tyre-health status.</CardDescription>
              </div>
              <StatePill value={tyreData?.worst_condition || overview.tyres?.status || 'unavailable'} />
            </CardHeader>
            <CardContent><SectionState section={overview.tyres} onRefresh={reload}><TyrePlan section={overview.tyres} onNavigate={navigateAction} canConfigure={isAdmin && tyreWorkspaceAvailable} canOpenWorkspace={tyreWorkspaceAvailable} /></SectionState></CardContent>
          </Card>
        </div>

        <Card id="usage-upkeep" className="vd-section-anchor">
          <CardHeader className="flex-col gap-4 space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Usage & upkeep</CardTitle>
              <CardDescription className="mt-1">Verified movement, fuel, service, and downtime.</CardDescription>
            </div>
            <div className="flex w-fit rounded-lg bg-slate-100 p-1" role="group" aria-label="Usage period">
              {['7d', '30d', '90d'].map((item) => <Button key={item} type="button" size="sm" variant={period === item ? 'default' : 'ghost'} aria-pressed={period === item} onClick={() => setPeriod(item)}>{item}</Button>)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" role="group" aria-label="Vehicle usage and upkeep metrics">
              <OperatingMetric icon={vehicle.vehicle_type === 'Non Movable' ? TimerReset : Gauge} label="Verified usage" value={usage ? `${formatNumber(usage.usage_value, { maximumFractionDigits: 1 })} ${usage.usage_unit}` : 'Unavailable'} note={usage ? `${usage.verified_day_count}/${usage.expected_day_count} verified days` : overview.usage?.reason} tone={usage ? 'emerald' : 'slate'} />
              <OperatingMetric icon={Fuel} label="Fuel issued" value={usage?.fuel_volume_litres ? `${formatNumber(usage.fuel_volume_litres, { maximumFractionDigits: 1 })} L` : 'Unavailable'} note={usage?.efficiency_value ? `${usage.efficiency_value} ${usage.efficiency_unit}` : 'Efficiency unavailable'} tone={usage?.fuel_volume_litres ? 'emerald' : 'slate'} />
              <OperatingMetric icon={Wrench} label="Service status" value={serviceValue} note={overview.service?.reason || 'Policy is active.'} tone={serviceTone} />
              <OperatingMetric icon={AlertTriangle} label="Downtime events" value={`${breakdowns?.period_count ?? '—'} events`} note={breakdowns ? `${breakdowns.open_count} currently open` : overview.breakdowns?.reason} tone={breakdownTone} />
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-5">
              {canService && (
              <Button onClick={() => setServiceOpen(true)}>
                <Wrench />
                Record service
              </Button>
              )}
              {data.actions.find((action) => action.id === 'open_fleet_statistics') && (
              <Button variant="outline" onClick={() => navigateAction(data.actions.find((action) => action.id === 'open_fleet_statistics'))}>
                <Activity />
                Open fleet statistics
              </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid items-start gap-5 lg:grid-cols-[1.15fr_0.85fr]">
          <Card id="activity" className="vd-section-anchor">
            <CardHeader>
              <CardTitle>Recent activity</CardTitle>
              <CardDescription>Audited changes and service events for this vehicle.</CardDescription>
            </CardHeader>
            <CardContent>
              <SectionState section={overview.activity} onRefresh={reload}>
                <ol className="vd-timeline">
                  {activity.map((event) => (
                    <li key={event.id}>
                      <span className="vd-timeline__marker" />
                      <div>
                        <p className="vd-mono vd-timeline__time">{formatDateTime(event.occurred_at)}</p>
                        <h3>{event.title}</h3>
                        <p>{event.detail}</p>
                        <span>
                          {event.origin?.label || 'System'}
                          {' '}
                          ·
                          {' '}
                          {titleCase(event.domain)}
                        </span>
                      </div>
                      {event.href && <Button variant="ghost" size="icon" aria-label={`Open ${event.title}`} onClick={() => history.push(event.href)}><ArrowUpRight /></Button>}
                    </li>
                  ))}
                  {!activity.length && (
                  <li className="vd-timeline__empty">
                    <CircleGauge />
                    <p>No audited vehicle events have been recorded yet.</p>
                  </li>
                  )}
                </ol>
              </SectionState>
            </CardContent>
          </Card>

          <Card id="vehicle-record" className="vd-section-anchor">
            <CardHeader className="flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle>Vehicle information</CardTitle>
                <CardDescription>Identity and operating details used in day-to-day work.</CardDescription>
              </div>
              {canEdit && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/app/vehicles/vehicle-list/${vehicle.id}/edit`}>
                  <Pencil />
                  Update information
                </Link>
              </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="vd-record-sections">
                <section>
                  <div className="vd-record-section__title">
                    <ClipboardList />
                    <h3>Vehicle identity</h3>
                  </div>
                  <DetailList items={identityItems} />
                </section>
                <section>
                  <div className="vd-record-section__title">
                    <Fuel />
                    <h3>Fuel & operation</h3>
                  </div>
                  <DetailList items={operationItems} />
                </section>
              </div>
              <div className="vd-record-footnote">
                <span>
                  Vehicle status:
                  {' '}
                  <strong>{fieldValue('active') || (vehicle.active ? 'Yes' : 'No')}</strong>
                </span>
                <span>
                  Last updated:
                  {' '}
                  <strong>{fieldValue('updated_at') ? formatDateTime(fieldValue('updated_at')) : 'Not recorded'}</strong>
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <RecordServiceDialog open={serviceOpen} onOpenChange={setServiceOpen} vehicleId={vehicle.id} service={overview.service} onSaved={reload} />
      <VehicleStatusDialog open={statusOpen} onOpenChange={setStatusOpen} vehicle={vehicle} onSaved={reload} />
    </main>
  );
};

export default VehicleDetailsV2;
